/**
 * iMessage-style bubble outlines as SVG paths.
 *
 * Geometry follows Apple's Messages bubble at 17pt text (radius 18, tail ~7 wide), scaled by
 * `u = radius / 18` so the shape keeps its proportions at any text size:
 * - corners: full radius, so one-line bubbles read as a pill
 * - tail (incoming, bottom-left): the left edge sweeps out in a concave curve to a point level
 *   with the bubble's bottom, and the underside curves back up into the rounded corner
 */
export function bubblePath(w: number, h: number, r: number, tail: boolean): string {
  const R = Math.min(r, h / 2, w / 2);
  const top = `M${R} 0 H${w - R} A${R} ${R} 0 0 1 ${w} ${R} V${h - R} A${R} ${R} 0 0 1 ${w - R} ${h}`;
  if (!tail) {
    return `${top} H${R} A${R} ${R} 0 0 1 0 ${h - R} V${R} A${R} ${R} 0 0 1 ${R} 0 Z`;
  }
  const u = R / 18;
  const p = (x: number, y: number) => `${x * u} ${h - y * u}`; // x right, y up from the bottom edge
  return [
    top,
    `H${18 * u}`,
    // rounded corner, cut short where the tail's underside takes over
    `C${p(14, 0)} ${p(11, 1.2)} ${p(8.6, 3.2)}`,
    // tail underside, curving down to the bottom line
    `C${p(5, 0.6)} ${p(1, 0)} ${p(-3, 0)}`,
    // point
    `L${p(-7, 0)}`,
    // concave sweep back up into the left edge
    `C${p(-3, 1.5)} ${p(0, 5)} ${p(0, 10)}`,
    `V${R} A${R} ${R} 0 0 1 ${R} 0 Z`,
  ].join(' ');
}

/** How far the tail's point sits left of the bubble's left edge (px) */
export const tailReach = (r: number, h: number) => (Math.min(r, h / 2) / 18) * 7;
